import type { Media } from '@prisma/client'
import { useTranslation } from 'react-i18next'

import { SelectionBar } from '~/components/drawer-layout'
import { Image } from '~/components/Media/Image'
import { Video } from '~/components/Media/Video'
import { MediaCard } from '~/components/media-card'
import type { AttachmentInfo } from '~/components/MediaManager/hooks/useAttachmentManager'
import { cn } from '~/utils/classnames'

interface SelectedMediaBarProps {
  attachments: AttachmentInfo[]
  placeholders: string[]
  isAttachmentsDirty: boolean
  isNewSelection: boolean
  isUpdating: boolean
  onReorder: (items: AttachmentInfo[]) => void
  onRemove: (mediaId: string) => void
  onSave: () => void
}

export function SelectedMediaBar({
  attachments,
  placeholders,
  isAttachmentsDirty,
  isNewSelection,
  isUpdating,
  onReorder,
  onRemove,
  onSave,
}: SelectedMediaBarProps) {
  const { t } = useTranslation()

  const hasAttachments = attachments.length > 0
  const canSave = isAttachmentsDirty || (isNewSelection && hasAttachments)

  return (
    <SelectionBar>
      <SelectionBar.Actions>
        <SelectionBar.DragHint />

        <SelectionBar.SaveButton
          onClick={onSave}
          isLoading={isUpdating}
          disabled={!canSave}
        >
          {isNewSelection
            ? hasAttachments
              ? t('components.media_manager.add_x_selected_assets', {
                  nSelected: attachments.length,
                })
              : t('components.media_manager.no_assets_selected')
            : t('action.save')}
        </SelectionBar.SaveButton>
      </SelectionBar.Actions>

      <SelectionBar.Items>
        <SelectionBar.ReorderGroup
          items={attachments}
          getItemId={(att) => att.media.id}
          onReorder={onReorder}
        >
          {(attachment) => (
            <SelectedMediaItem
              media={attachment.media}
              onRemove={() => onRemove(attachment.media.id)}
            />
          )}
        </SelectionBar.ReorderGroup>

        <SelectionBar.Placeholder count={placeholders.length} />
      </SelectionBar.Items>
    </SelectionBar>
  )
}

interface SelectedMediaItemProps {
  media: Media
  onRemove: () => void
}

function SelectedMediaItem({ media, onRemove }: SelectedMediaItemProps) {
  return (
    <MediaCard format="square" className={cn('xs:h-24 h-16', 'aspect-square')}>
      <MediaCard.Cover>
        {media.kind === 'image' ? (
          <Image
            src={media.src}
            transformation={{
              width: 200,
              format: 'webp',
              resizeMode: 'fill',
            }}
            sizes={{ default: '150px' }}
            className={cn(
              'touch-callout-none touch-action-none object-cover',
              'aspect-square'
            )}
          />
        ) : (
          <Video
            src={media.src}
            className={cn(
              'touch-callout-none touch-action-none object-cover',
              'aspect-square'
            )}
          />
        )}
      </MediaCard.Cover>

      <MediaCard.CloseButton
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      />
    </MediaCard>
  )
}
